// ============================================================
//  FEATURE: Prescription  (derived from Receipt Guestbook)
//  An AMBULATE PHARMACY thermal Rx printer. No text input —
//  purely generative. A skeuomorphic printer head with a
//  PRINT Rx button; each click prints a fresh thermal slip
//  that slides out + tears off, bearing a randomized surreal
//  prescription: Rx drug line, SIG/directions, refills, a
//  diagnosis line, a fake barcode + serial. Printed slips
//  stack; the top slip can be torn off (removed).
//
//  PERF: there is NO idle loop, timer, canvas or rAF. The only
//  animation is a one-shot CSS slide/tear keyed off the freshest
//  slip's id, so it runs once per print and then rests. Pointer
//  handlers are kept trivial; prefersReducedMotion() collapses
//  the print animation to an instant appearance.
//
//  Self-contained. No network. Mount standalone or inside a
//  window (programText="RX-PRINTER.RCP").
// ============================================================

import * as React from "react";
import { useState, useCallback } from "react";
import { prefersReducedMotion } from "../_shared/sketchFrame";
import data from "./prescriptions.json";
import "./prescription.css";

const DIV = "* * * * * * * * * * * * * * * *";

function pick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

// zero-padded random integer as a string
function pad(n, len) {
    let s = String(n);
    while (s.length < len) s = "0" + s;
    return s;
}

// Build the bar widths for one barcode ONCE, at slip-creation time, so a
// printed slip's barcode is immutable (it must not reshuffle every render).
function makeBars() {
    const bars = [];
    for (let i = 0; i < 38; i++) {
        bars.push(1 + Math.floor(Math.random() * 4));
    }
    return bars;
}

// Build one fully-randomized surreal prescription record.
function makeRx() {
    const drug = pick(data.drugs);
    const form = pick(data.forms);
    const dose = pick(data.doses);
    const refills = Math.floor(Math.random() * 100); // 0..99, "AS NEEDED" sometimes
    const rxNo = pad(Math.floor(Math.random() * 9000000) + 1000000, 7);
    const serial = pad(Math.floor(Math.random() * 1e6), 6);
    return {
        bars: makeBars(),
        id: Date.now() + Math.floor(Math.random() * 1000),
        ts: new Date().toLocaleString(),
        drug,
        form,
        dose,
        sig:
            pick(data.sigVerbs) +
            " " +
            pick(data.sigQty) +
            " " +
            form.toUpperCase() +
            " " +
            pick(data.sigWhen),
        refills:
            refills === 0
                ? "AS NEEDED (∞)"
                : String(refills) + (refills === 1 ? " (ONE)" : ""),
        warning: pick(data.warnings),
        diagnosis: pick(data.diagnoses),
        prescriber: pick(data.prescribers),
        rxNo,
        serial,
    };
}

// A torn barcode strip of variable-width black bars. The bar widths are
// frozen on the record at slip-creation time (rx.bars), so the barcode is
// stable across re-renders (printing/tearing another slip will not reshuffle
// an already-printed slip's barcode).
function Barcode({ bars }) {
    return (
        <div className="rx-barcode">
            {bars.map((w, i) => (
                <div
                    key={i}
                    className="rx-bar"
                    style={{ width: w + "px", opacity: i % 6 === 0 ? 0.55 : 1 }}
                />
            ))}
        </div>
    );
}

function Prescription() {
    const [slips, setSlips] = useState([]);
    // id of the slip currently playing its one-shot print animation
    const [printingId, setPrintingId] = useState(null);

    const handlePrint = useCallback(() => {
        const rx = makeRx();
        setSlips((prev) => [rx, ...prev]);
        // Gate the slide/tear animation on reduced-motion preference.
        if (!prefersReducedMotion()) {
            setPrintingId(rx.id);
        }
    }, []);

    // When the one-shot print animation ends, clear the flag so no
    // animation state lingers (and nothing loops).
    const handleAnimEnd = useCallback(
        (id) => {
            if (id === printingId) setPrintingId(null);
        },
        [printingId]
    );

    // Tear off (remove) the top-most slip.
    const handleTear = useCallback((id) => {
        setSlips((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const topId = slips.length ? slips[0].id : null;

    return (
        <div className="rx-shell">
            {/* ---------- PRINTER HEAD ---------- */}
            <div className="rx-printer" aria-hidden="false">
                <div className="rx-printer-brand">AMBULATE PHARMACY</div>
                <div className="rx-printer-sub">Rx THERMAL DISPENSARY UNIT</div>
                <div className="rx-vents">
                    <span /><span /><span /><span /><span /><span />
                </div>
                {/* the paper slot the slip emerges from */}
                <div className="rx-slot" />
                <button
                    className="rx-btn"
                    type="button"
                    onClick={handlePrint}
                    aria-label="Print a new prescription"
                >
                    &gt;&gt; PRINT Rx &lt;&lt;
                </button>
            </div>

            {/* ---------- STACK OF PRINTED SLIPS ---------- */}
            <div className="rx-stack">
                {slips.map((rx, idx) => {
                    const isTop = idx === 0;
                    const isPrinting = rx.id === printingId;
                    return (
                        <div
                            className={
                                "rx-slip" + (isPrinting ? " rx-printing" : "")
                            }
                            key={rx.id}
                            onAnimationEnd={() => handleAnimEnd(rx.id)}
                        >
                            <div className="rx-center rx-shout">AMBULATE PHARMACY</div>
                            <div className="rx-center rx-dim">
                                24HR DISPENSARY // NO REFUNDS
                            </div>
                            <div className="rx-center rx-div">{DIV}</div>

                            <div className="rx-line">
                                <span className="rx-rxmark">℞</span>
                                <span className="rx-drug">{rx.drug}</span>
                            </div>
                            <div className="rx-line rx-dim">
                                {rx.dose} &middot; {rx.form}
                            </div>

                            <div className="rx-center rx-div">{DIV}</div>

                            <div className="rx-key">SIG / DIRECTIONS</div>
                            <div className="rx-sig">{rx.sig}</div>

                            <div className="rx-row">
                                <span className="rx-key">REFILLS</span>
                                <span className="rx-val">{rx.refills}</span>
                            </div>
                            <div className="rx-row">
                                <span className="rx-key">Rx #</span>
                                <span className="rx-val">{rx.rxNo}</span>
                            </div>

                            <div className="rx-center rx-div">{DIV}</div>

                            <div className="rx-line">
                                DX:&nbsp;
                                <span className="rx-dx">{rx.diagnosis}</span>
                            </div>
                            <div className="rx-warning">! {rx.warning} !</div>

                            <Barcode bars={rx.bars} />
                            <div className="rx-center rx-serial">
                                SER {rx.serial}
                            </div>

                            <div className="rx-center rx-div">{DIV}</div>
                            <div className="rx-sign">{rx.prescriber}</div>
                            <div className="rx-center rx-ts">{rx.ts}</div>

                            {/* tear-off only enabled on the top slip */}
                            {isTop ? (
                                <button
                                    className="rx-tear"
                                    type="button"
                                    onClick={() => handleTear(rx.id)}
                                    aria-label="Tear off this prescription"
                                >
                                    ✂ - - - TEAR OFF - - -
                                </button>
                            ) : (
                                <div className="rx-tearline" aria-hidden="true">
                                    - - - - - - - - - - - - - -
                                </div>
                            )}
                        </div>
                    );
                })}

                {slips.length === 0 ? (
                    <div className="rx-empty">
                        <div className="rx-empty-line">NO Rx ON FILE</div>
                        <div className="rx-empty-sub">
                            press PRINT Rx to dispense
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default Prescription;
